import { useCallback, useEffect } from 'react'
import type { MutableRefObject, RefObject, Dispatch, SetStateAction } from 'react'
import type { AgGridReact } from 'ag-grid-react'
import type { TableRow } from './types'
import { createEmptyRow, rowStore } from './tableData'
import type { SaveFieldArgs } from './useEditingEngine'

/**
 * RFC 4180 TSV parser — корректно обрабатывает ячейки с переносами строк,
 * обёрнутые в кавычки ("...\n..."), и экранированные кавычки ("").
 */
export function parseTsvRfc4180(text: string): string[][] {
  const rows: string[][] = []
  let row: string[] = []
  let cell = ''
  let i = 0
  const n = text.length

  while (i < n) {
    const ch = text[i]!

    if (ch === '"') {
      i++
      while (i < n) {
        const c = text[i]!
        if (c === '"') {
          if (text[i + 1] === '"') {
            cell += '"'
            i += 2
          } else {
            i++
            break
          }
        } else {
          cell += c
          i++
        }
      }
    } else if (ch === '\t') {
      row.push(cell)
      cell = ''
      i++
    } else if (ch === '\r' && text[i + 1] === '\n') {
      row.push(cell)
      cell = ''
      rows.push(row)
      row = []
      i += 2
    } else if (ch === '\n') {
      row.push(cell)
      cell = ''
      rows.push(row)
      row = []
      i++
    } else {
      cell += ch
      i++
    }
  }

  if (row.length > 0 || cell !== '') {
    row.push(cell)
    rows.push(row)
  }

  const last = rows[rows.length - 1]
  if (last && last.length === 1 && last[0] === '') {
    rows.pop()
  }

  return rows
}

export function useTableClipboard({
  gridRef,
  containerRef,
  selRef,
  extraCellsRef,
  getColIdx,
  setRowData,
  saveField,
}: {
  gridRef: RefObject<AgGridReact<TableRow>>
  containerRef: RefObject<HTMLDivElement>
  selRef: MutableRefObject<{ r1: number; r2: number; c1: number; c2: number } | null>
  extraCellsRef: MutableRefObject<Set<string>>
  getColIdx: (colId: string) => number
  setRowData: Dispatch<SetStateAction<TableRow[]>>
  /** Персист изменённой ячейки на сервер (HIGH-9). saveField сам фильтрует
   *  локальные поля, новые пустые строки и пустой партномер. */
  saveField: (args: SaveFieldArgs) => Promise<void> | void
}) {
  // HIGH-9: сохраняет пачку изменённых ячеек на сервер с лимитом конкуренции,
  // чтобы массовая вставка/очистка не выстрелила сотней PATCH разом. Раньше
  // paste/Delete меняли только локальный state → данные молча терялись при
  // ближайшем рефетче. saveField внутри умеет retry/откат/тост.
  const persist = useCallback(
    (pending: SaveFieldArgs[]) => {
      const changed = pending.filter((p) => p.newValue !== p.oldValue)
      if (changed.length === 0) return
      const CONCURRENCY = 4
      let idx = 0
      const worker = async () => {
        while (idx < changed.length) {
          const item = changed[idx++]!
          await saveField(item)
        }
      }
      void Promise.all(Array.from({ length: Math.min(CONCURRENCY, changed.length) }, worker))
    },
    [saveField],
  )

  // HIGH-9: имя поля по ВИЗУАЛЬНОМУ индексу колонки берём из ЖИВОГО порядка
  // колонок (тот же источник, что getColIdx) — иначе под кастомным порядком/
  // закреплением и для роли PROCUREMENT (где colDefs вырезает clientName/status)
  // статичный COL_FIELDS съезжает и paste/Delete писали бы в ЧУЖОЕ поле БД.
  // Бонус: так корректно резолвятся proc-слоты (proc_<userId>).
  const fieldAt = useCallback(
    (idx: number): string | undefined => gridRef.current?.api.getColumns()?.[idx]?.getColId(),
    [gridRef],
  )

  const applyPastedRows = useCallback(
    (parsedRows: string[][], startRow: number, startCol: number) => {
      // Пенды собираем из текущего состояния grid'а СИНХРОННО (не из setState-
      // updater'а, иначе словим тайминг). id берём у отображаемой строки —
      // именно её правит пользователь. Новые пустые строки (gridRow=undefined)
      // не персистим: их ещё нет в БД.
      const pending: SaveFieldArgs[] = []
      parsedRows.forEach((cols, rowOffset) => {
        const gridRow = gridRef.current?.api.getDisplayedRowAtIndex(startRow + rowOffset)?.data
        if (!gridRow) return
        cols.forEach((cellVal, colOffset) => {
          const field = fieldAt(startCol + colOffset)
          if (!field || field === 'id') return
          pending.push({
            id: gridRow.id,
            field: field as keyof TableRow,
            newValue: cellVal,
            oldValue: (gridRow as unknown as Record<string, unknown>)[field],
          })
        })
      })

      setRowData((prev) => {
        const next = [...prev]
        parsedRows.forEach((cols, rowOffset) => {
          const rowIdx = startRow + rowOffset
          while (next.length <= rowIdx) next.push(createEmptyRow())
          const gridRow = gridRef.current?.api.getDisplayedRowAtIndex(rowIdx)?.data
          const row = { ...(gridRow ?? next[rowIdx]!) }
          cols.forEach((cellVal, colOffset) => {
            const field = fieldAt(startCol + colOffset)
            if (!field || field === 'id') return
            ;(row as Record<string, unknown>)[field] = cellVal
          })
          next[rowIdx] = row as TableRow
        })
        const last = next[next.length - 1]
        const isEmpty =
          last && Object.entries(last).every(([k, v]) => k === 'id' || v === '' || v === null)
        if (!isEmpty) next.push(createEmptyRow())
        rowStore.current = next
        return next
      })

      persist(pending)
    },
    [gridRef, setRowData, persist, fieldAt],
  )

  // Ctrl+C копирование, Ctrl+V вставка, Delete/Backspace очистка
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const editing = (gridRef.current?.api.getEditingCells() ?? []).length > 0

      // Ctrl+C — скопировать выделение в буфер.
      // e.code вместо e.key: проверяем физическую клавишу. e.key при русской
      // раскладке даёт 'с' (кириллическая «эс») → копирование не работает.
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyC' && !editing) {
        const allKeys = new Set(extraCellsRef.current)
        const sel = selRef.current
        if (sel) {
          for (let r = sel.r1; r <= sel.r2; r++)
            for (let c = sel.c1; c <= sel.c2; c++) allKeys.add(`${r}:${c}`)
        }
        if (allKeys.size === 0) return
        let minR = Infinity,
          maxR = -Infinity,
          minC = Infinity,
          maxC = -Infinity
        for (const key of allKeys) {
          const [kr, kc] = key.split(':').map(Number)
          if (kr < minR) minR = kr
          if (kr > maxR) maxR = kr
          if (kc < minC) minC = kc
          if (kc > maxC) maxC = kc
        }
        const rows: string[] = []
        for (let r = minR; r <= maxR; r++) {
          const node = gridRef.current?.api.getDisplayedRowAtIndex(r)
          const cells: string[] = []
          for (let c = minC; c <= maxC; c++) {
            if (!allKeys.has(`${r}:${c}`)) {
              cells.push('')
            } else {
              const field = fieldAt(c)
              const val = field ? node?.data?.[field as keyof TableRow] : ''
              cells.push(val == null ? '' : String(val))
            }
          }
          rows.push(cells.join('\t'))
        }
        void navigator.clipboard.writeText(rows.join('\n'))
        return
      }

      // Delete / Backspace — очистить ячейки
      if ((e.key === 'Delete' || e.key === 'Backspace') && !editing) {
        const allKeys = new Set(extraCellsRef.current)
        const sel = selRef.current
        if (sel) {
          for (let r = sel.r1; r <= sel.r2; r++)
            for (let c = sel.c1; c <= sel.c2; c++) allKeys.add(`${r}:${c}`)
        }
        if (allKeys.size === 0) return
        e.preventDefault()
        // HIGH-9: собираем пенды из отображаемых строк СИНХРОННО для персиста.
        const pending: SaveFieldArgs[] = []
        for (const key of allKeys) {
          const [r, c] = key.split(':').map(Number)
          const gridRow = gridRef.current?.api.getDisplayedRowAtIndex(r!)?.data
          if (!gridRow) continue
          const field = fieldAt(c!)
          if (!field || field === 'id') continue
          const cleared =
            field === 'qty' || field === 'totalY' || field === 'targetPrice' ? null : ''
          pending.push({
            id: gridRow.id,
            field: field as keyof TableRow,
            newValue: cleared,
            oldValue: (gridRow as unknown as Record<string, unknown>)[field],
          })
        }
        setRowData((prev) => {
          const next = [...prev]
          for (const key of allKeys) {
            const [r, c] = key.split(':').map(Number)
            if (r >= next.length) continue
            const row = { ...next[r]! }
            const field = fieldAt(c)
            if (!field || field === 'id') continue
            ;(row as Record<string, unknown>)[field] =
              field === 'qty' || field === 'totalY' || field === 'targetPrice' ? null : ''
            next[r] = row as TableRow
          }
          rowStore.current = next
          return next
        })
        persist(pending)
        return
      }

      // Ctrl+V — вставить из буфера
      // FIX: stopPropagation предотвращает обработку Ctrl+V самим AG Grid.
      // e.code вместо e.key — раскладка-независимо (русская «м» при Ctrl+V).
      if ((e.ctrlKey || e.metaKey) && e.code === 'KeyV' && !editing) {
        e.preventDefault()
        e.stopPropagation()
        void navigator.clipboard
          .readText()
          .then((text) => {
            if (!text) return
            const parsedRows = parseTsvRfc4180(text)
            if (parsedRows.length === 0) return
            const focused = gridRef.current?.api.getFocusedCell()
            if (!focused) return
            const sel = selRef.current
            // Если в буфере одна ячейка и выделен диапазон — заполняем весь диапазон
            if (
              parsedRows.length === 1 &&
              parsedRows[0]!.length === 1 &&
              sel &&
              (sel.r2 > sel.r1 || sel.c2 > sel.c1)
            ) {
              const val = parsedRows[0]![0]!
              const filled: string[][] = []
              for (let r = sel.r1; r <= sel.r2; r++) {
                const rowArr: string[] = []
                for (let c = sel.c1; c <= sel.c2; c++) rowArr.push(val)
                filled.push(rowArr)
              }
              applyPastedRows(filled, sel.r1, sel.c1)
              return
            }
            applyPastedRows(parsedRows, focused.rowIndex, getColIdx(focused.column.getColId()))
          })
          .catch(() => {
            // readText() отклонён браузером — нет разрешения
          })
        return
      }
    }

    document.addEventListener('keydown', onKey, true)
    return () => document.removeEventListener('keydown', onKey, true)
  }, [applyPastedRows, extraCellsRef, getColIdx, gridRef, selRef, setRowData, persist, fieldAt])

  // Paste-событие на контейнере (альтернативный путь вставки через правую кнопку/меню)
  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const handlePaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData('text/plain') ?? ''
      if (!text) return
      const parsedRows = parseTsvRfc4180(text)
      if (parsedRows.length === 0) return
      const editing = (gridRef.current?.api.getEditingCells() ?? []).length > 0
      // Одна ячейка в режиме редактирования — пусть textarea вставляет сам
      if (editing && parsedRows.length === 1 && parsedRows[0]!.length === 1) return
      e.preventDefault()
      const focused = gridRef.current?.api.getFocusedCell()
      if (!focused) return
      applyPastedRows(parsedRows, focused.rowIndex, getColIdx(focused.column.getColId()))
    }
    el.addEventListener('paste', handlePaste, true)
    return () => el.removeEventListener('paste', handlePaste, true)
  }, [applyPastedRows, containerRef, getColIdx, gridRef])

  return { applyPastedRows }
}
